import { useState } from 'react';
import { where } from 'firebase/firestore';
import {
    Contract,
    Section,
    Column,
    Clause,
    SECTION_COLLECTION_NAME,
    CLAUSE_COLLECTION_NAME,
    ContractSection,
    Segment,
} from 'models';
import { getNextFields, OrderBy } from 'components/settings/recordList';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { CollectionState, useCollectionContext } from 'components/settings/collectionContext';
import { useSubsection } from './contractClauses';
import { useContractContext } from './contractContext';
import './contract.css';
import { NavLink } from 'react-router-dom';

interface SubsectionProps {
    indexId: string;
    section: Section;
}
const SubsectionItem = ({ indexId, section }: SubsectionProps) => {
    const { contract, setContractSections } = useContractContext();
    const subsection = useSubsection(indexId);
    if (!subsection) {
        return <div>Cannot find subsection '{indexId}'</div>;
    }
    return (
        <div
            key={subsection.name}
            style={{ display: 'flex', marginLeft: '25px', alignItems: 'center' }}
        >
            <AddOutlinedIcon
                style={{ color: '#747474', fontSize: '18px', cursor: 'pointer' }}
                onClick={() =>
                    setContractSections(
                        contract.contractSections.map((contractSection) =>
                            contractSection.sectionIndexId === section.indexId()
                                ? {
                                      ...contractSection,
                                      segments: contractSection.segments.concat([
                                          new Segment({ subsectionIndexId: indexId }),
                                      ]),
                                  }
                                : contractSection
                        )
                    )
                }
            />
            <div>{subsection.name}</div>
            <div>{subsection.title}</div>
        </div>
    );
};

interface SubsectionsProps {
    section: Section;
    expanded?: boolean;
    setExpanded: (_?: boolean) => void;
}
const Subsections = ({ section, expanded, setExpanded }: SubsectionsProps) => {
    if (!section.subsectionIndexIds.length) {
        return <div></div>;
    }
    return (
        <div>
            <div
                style={{
                    marginLeft: '28px',
                    marginTop: '6px',
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
                onClick={() => setExpanded(!expanded)}
            >
                Sub-sections
                <div style={{ marginTop: '2px' }}>
                    {expanded ? (
                        <ArrowLeftIcon htmlColor="#818181" />
                    ) : (
                        <ArrowDropDownIcon htmlColor="#818181" />
                    )}
                </div>
            </div>
            {expanded &&
                section.subsectionIndexIds.map((subsectionIndexId: string) => (
                    <SubsectionItem
                        key={subsectionIndexId}
                        indexId={subsectionIndexId}
                        section={section}
                    />
                ))}
        </div>
    );
};

const applyClauseChange = (
    contractSection: ContractSection,
    clause: Clause,
    selected: boolean
): ContractSection => {
    const clauseIndexId = clause.indexId();
    const segments = selected
        ? contractSection.segments
              .filter((segment) => segment.clauseIndexId !== clauseIndexId)
              .map((segment) => {
                  return new Segment({
                      ...segment,
                      clauseIndexIds: segment
                          .getClauses()
                          .filter((clauseIndexId_) => clauseIndexId_ !== clauseIndexId),
                  });
              })
        : contractSection.segments.concat([new Segment({ clauseIndexId })]);
    return { ...contractSection, segments };
};

interface ClausesProps {
    expanded?: boolean;
    setExpanded: (_?: boolean) => void;
    section: Section;
    existingSection?: ContractSection;
}
const Clauses = ({ expanded, setExpanded, section, existingSection }: ClausesProps) => {
    const { contract, setContractSections } = useContractContext();
    const [allClauses] = useCollectionContext(CLAUSE_COLLECTION_NAME, [
        where('productTypeIndexIds', 'array-contains', contract.productTypeIndexId),
    ]) as CollectionState<Clause>;
    const indexId = section.indexId();
    const clauses = allClauses ? allClauses.filter((c) => c.sectionIndexId === indexId) : [];
    if (!clauses.length) {
        return <div></div>;
    }
    return (
        <div>
            <div
                style={{
                    marginLeft: '28px',
                    marginTop: '6px',
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
                onClick={() => setExpanded(!expanded)}
            >
                Clauses
                <div style={{ marginTop: '2px' }}>
                    {expanded ? (
                        <ArrowLeftIcon htmlColor="#818181" />
                    ) : (
                        <ArrowDropDownIcon htmlColor="#818181" />
                    )}
                </div>
            </div>
            {expanded &&
                clauses.map((clause: Clause) => {
                    const clauseIndexId = clause.indexId();
                    const selected = !!existingSection?.segments.find(
                        (segment) =>
                            segment.clauseIndexId === clauseIndexId ||
                            segment
                                .getClauses()
                                .find((clauseIndexId_) => clauseIndexId_ === clauseIndexId)
                    );
                    return (
                        <div
                            key={clause.name}
                            style={{ display: 'flex', marginLeft: '25px', alignItems: 'center' }}
                        >
                            <input
                                title={existingSection ? '' : `Add the section '${section.name}'`}
                                type="checkbox"
                                placeholder=""
                                checked={selected}
                                disabled={!existingSection}
                                style={{ marginRight: '11px' }}
                                onChange={() =>
                                    setContractSections(
                                        contract.contractSections.map((contractSection) =>
                                            contractSection.sectionIndexId === indexId
                                                ? applyClauseChange(
                                                      contractSection,
                                                      clause,
                                                      selected
                                                  )
                                                : contractSection
                                        )
                                    )
                                }
                            />
                            <div>{clause.name}</div>
                            <div>{clause.title}</div>
                        </div>
                    );
                })}
        </div>
    );
};

interface SectionItemProps {
    record: Section;
}
const SectionItem = ({ record }: SectionItemProps) => {
    const { contract, setContract } = useContractContext();
    const [clausesExpanded, _setClausesExpanded] = useState(record.clausesExpanded);
    const [subsectionsExpanded, _setSubsectionsExpanded] = useState(record.clausesExpanded);
    const indexId = record.indexId();
    const existingSection = contract.contractSections.find((c) => c.sectionIndexId === indexId);
    const checked = !!existingSection?.active;
    const onCheck = () => {
        const nt = { sectionIndexId: indexId, segments: [], active: true };
        const contractSections = existingSection?.active
            ? contract.contractSections.map((c) => {
                  return { ...c, active: c.sectionIndexId === indexId ? false : c.active };
              })
            : existingSection
            ? contract.contractSections.map((c) => {
                  return { ...c, active: c.sectionIndexId === indexId ? true : c.active };
              })
            : contract.contractSections.concat([nt]);
        return setContract(new Contract({ ...contract, contractSections }));
    };
    const setClausesExpanded = () => {
        record.clausesExpanded = !clausesExpanded;
        _setClausesExpanded(record.clausesExpanded);
    };
    const setSubsectionsExpanded = () => {
        record.subsectionsExpanded = !subsectionsExpanded;
        _setSubsectionsExpanded(record.subsectionsExpanded);
    };
    return (
        <div key={record.id} style={{ margin: '8px 11px 1px 11px' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <input onChange={onCheck} checked={checked} type="checkbox" placeholder="" />
                {checked ? (
                    <NavLink
                        style={{ color: 'inherit', textDecoration: 'inherit' }}
                        to={`${window.location.pathname}#${record.__type}-${record.id}`}
                    >
                        {record.name}
                    </NavLink>
                ) : (
                    <div>{record.name}</div>
                )}
            </div>
            <Subsections
                expanded={subsectionsExpanded}
                setExpanded={setSubsectionsExpanded}
                section={record}
            />
            <Clauses
                expanded={clausesExpanded}
                setExpanded={setClausesExpanded}
                section={record}
                existingSection={existingSection}
            />
        </div>
    );
};

const SectionList = () => {
    const [sections, sectionsPending] = useCollectionContext(
        SECTION_COLLECTION_NAME
    ) as CollectionState<Section>;

    if (sectionsPending) {
        return (
            <div className="contractClausesBox">
                <div style={styles}>Sections</div>
                <div className="contractClausesBoxList">Pending...</div>
            </div>
        );
    }
    return (
        <div style={{ paddingBottom: '7px' }}>
            {sections?.map((section) => (
                <SectionItem record={section} key={section.id} />
            ))}
        </div>
    );
};

const clauseColumns: Column[] = [
    {
        field: 'createdAt',
        label: 'Date',
        filter: true,
        direction: 'desc',
        fieldType: 'createdAt',
    },
    {
        field: 'name',
        label: 'A-Z',
        filter: true,
        selected: true,
        direction: 'asc',
    },
];

const styles = {
    background: '#F4F4F4',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    padding: '0 14px',
    justifyContent: 'space-between',
};

export const SectionsSidePanel = () => {
    const [fields, setFields] = useState<Column[]>(clauseColumns.filter((column) => column.filter));
    const onFieldClick = (field: Column) => setFields(getNextFields(field, fields));
    return (
        <div className="contractClausesBox">
            <div style={styles}>
                <div>Sections</div>
                <div>
                    <OrderBy columns={fields} headers={''} onFieldClick={onFieldClick} />
                </div>
            </div>
            <div className="contractClausesBoxList">
                <SectionList />
            </div>
        </div>
    );
};
